import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle } from "lucide-react";

const ABOUT_FEATURES = [
  { id: "feature-qualified", text: "Fully qualified & insured" },
  { id: "feature-management", text: "End-to-end project management" },
  { id: "feature-communication", text: "Clear communication throughout" },
  { id: "feature-quotes", text: "Competitive fixed-price quotes" }
];

const ServicesPreview = ({ services }) => (
  <section className="py-20 md:py-32">
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
      <div className="text-center mb-16">
        <p className="text-sm font-medium tracking-wide uppercase text-amber-700 mb-4">
          What We Do
        </p>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-4">
          Our Core Services
        </h2>
        <p className="text-lg text-stone-600 max-w-2xl mx-auto">
          From small joinery jobs to complete home transformations, we handle 
          every project with the same dedication to quality.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
        {services.map((service) => (
          <div
            key={service.id}
            className="bg-white rounded-lg p-8 shadow-xl shadow-stone-200/50 hover:shadow-2xl transition-shadow border border-stone-100 group"
          >
            <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center mb-6 group-hover:bg-amber-700 transition-colors">
              <service.icon className="text-amber-700 group-hover:text-white transition-colors" size={28} />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3">{service.title}</h3>
            <p className="text-stone-600 leading-relaxed">{service.description}</p>
          </div>
        ))}
      </div>

      <div className="text-center">
        <Link to="/services">
          <Button 
            variant="outline" 
            className="h-12 px-8 font-semibold"
            data-testid="view-all-services-btn"
          >
            View All Services
            <ArrowRight className="ml-2" size={18} />
          </Button>
        </Link>
      </div>
    </div>
  </section>
);

const AboutPreview = () => (
  <section className="py-20 md:py-32 bg-white">
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div className="relative">
          <img
            src="/images/projects/Kitchen2.JPG"
            alt="Tony Payne - TJP Carpentry"
            className="rounded-lg shadow-2xl object-cover w-full h-[500px]"
            loading="lazy"
            decoding="async"
            width={600}
            height={500}
          />
          <div className="absolute -bottom-6 -right-6 bg-amber-700 rounded-lg p-6 text-white">
            <p className="text-4xl font-black">10+</p>
            <p className="text-sm text-amber-100">Years Experience</p>
          </div>
        </div>

        <div className="space-y-6">
          <p className="text-sm font-medium tracking-wide uppercase text-amber-700">
            About TJP Carpentry
          </p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
            Meet Tony Payne
          </h2>
          <p className="text-lg text-stone-600 leading-relaxed">
            A fully qualified carpenter with over 10 years of experience, Tony has 
            built his reputation on quality craftsmanship and exceptional customer 
            service. Having worked extensively as a kitchen and bathroom fitter, 
            he brings comprehensive expertise to every project.
          </p>
          <ul className="space-y-3">
            {ABOUT_FEATURES.map((feature) => (
              <li key={feature.id} className="flex items-center gap-3 text-stone-600">
                <CheckCircle className="text-green-600 flex-shrink-0" size={20} />
                <span>{feature.text}</span>
              </li>
            ))}
          </ul>
          <Link to="/about">
            <Button className="bg-slate-900 text-white hover:bg-slate-800 h-12 px-8 font-semibold mt-4">
              Learn More About Us
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  </section>
);

export { ServicesPreview, AboutPreview };
